import { Link } from 'react-router-dom';
import { Users, Heart, ShieldCheck, ArrowRight } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

const principles = [
  {
    icon: Users,
    title: 'Teams and systems, not individuals',
    description:
      'Our deepest signals - review networks, knowledge silos, bus factor - measure how the system works. You cannot fake a bus factor, and we never use them to rank people.',
  },
  {
    icon: Heart,
    title: 'Developmental, not evaluative',
    description:
      'Individual views exist for coaching and better one-on-ones, never for stack-ranking. The goal is to help the team improve, not to grade the people in it.',
  },
  {
    icon: ShieldCheck,
    title: 'Backed by research',
    description:
      'A 2024 peer-reviewed study found that surveillance-style monitoring raised complaints and lowered output. Framing analytics as developmental does the opposite.',
  },
];

export default function AntiSurveillanceStrip() {
  const [sectionRef, isInView] = useInView<HTMLElement>({ threshold: 0.1 });

  return (
    <section ref={sectionRef} className="py-14 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-10 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-green-400 mb-3">
            Built for trust
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Analytics your developers won&apos;t resist
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            The biggest barrier to engineering analytics is not the math. It is developer resistance.
            CodePulse measures teams and systems, not individuals - so leaders get visibility and
            developers get a tool they trust.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {principles.map((principle, index) => (
            <div
              key={principle.title}
              className={`rounded-2xl border border-gray-700 bg-gray-800/60 p-6 ${
                isInView ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 120}ms` }}
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-green-500/15 mb-4">
                <principle.icon className="w-5 h-5 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{principle.title}</h3>
              <p className="text-sm text-gray-300 leading-relaxed">{principle.description}</p>
            </div>
          ))}
        </div>

        <div className={`text-center mt-8 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <Link
            to="/github-analytics"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-green-400 hover:text-green-300 transition-colors"
          >
            See how team-first analytics works
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
